import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApiUrlService } from '@core/services/common/api-url.service';
import { CurrencyPreferenceService } from '@shared/services/currency/currency-preference.service';
import { ApiClientService } from '@core/services/client/api-client.service';
import { LocalStorageService } from '@shared/services/storage';

import { Basket } from './api-basket.service';

export interface OrderItem {
  type: string;
  objectId: number;
  quantity: number;
  price: number;
  currency: string;
}

export interface ShippingCosts {
  insurance?: string;
  euDelivery?: string;
  worldDelivery?: string;
  domesticDelivery?: string;
  collectionOnSite?: string;
}

export interface Order {
  paymentType: number;
  messageToCustomer: string;
  firstName: string;
  surname: string;
  company: string;
  nip: string;
  street: string;
  postCode: string;
  city: string;
  invoice: boolean;
  invoiceFirstName: string;
  invoiceSurname: string;
  invoiceCompany: string;
  invoiceNip: string;
  invoiceStreet: string;
  invoicePostCode: string;
  invoiceCity: string;
  items: OrderItem[];
  shippingCosts: ShippingCosts;
  shippingPackageMaximumElementsInPackage: string;
  shippingPackageType: string;
}

export interface OrderAsAuthorized {
  items: OrderItem[];
  shipping_type: string;
  payment_type: string;
  insurance: boolean;
  message_to_customer: string;
  first_name: string;
  name: string;
  street: string;
  post_code: string;
  phone_number: string;
  email: string;
  city: string;
  country_id: number;
  company_name: string;
  nip: string;
  invoice: boolean;
  invoice_first_name: string;
  invoice_name: string;
  invoice_company_name: string;
  invoice_nip: string;
  invoice_street: string;
  invoice_post_code: string;
  invoice_city: string;
}

export type PaymentProvider = 'payu' | 'stripe';

/**
 * Surowa mapa typów płatności (klucz => kod int), rozszerzona o meta-pole `provider`
 * (bramka obsługująca płatność online — kontrakt B9). `provider` NIE jest typem
 * płatności — konsument iterujący mapę musi go pominąć.
 *
 * `stripeMethods` to kolejne meta-pole (backend zwraca `stripe_methods`, globalny
 * interceptor camelCase'uje odpowiedź) — realne metody płatności Stripe (karta/BLIK/P24)
 * aktywne na koncie Connect galerii. Zawsze obecne w odpowiedzi; pusta tablica, gdy
 * aktywna bramka to nie Stripe albo galeria nie ma konta Connect.
 */
export interface Payments {
  cashOnDeliver?: number;
  online?: number;
  provider?: PaymentProvider | null;
  stripeMethods?: string[];
}

export interface PaymentLink {
  url: string;
}

@Injectable()
export class ApiOrderService {
  constructor(private httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    private localStorageService: LocalStorageService,
    private currencyPreferenceService: CurrencyPreferenceService) {
  }

  orderBaseOnUserBasket(): Observable<Order> {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient.get<Order>(
      this.currencyPreferenceService.appendCurrencyToUrl(`${aliUrl}client/order/order-object-base-on-basket`),
    );
  }

  orderBaseOnBasket(basket: Basket): Observable<Order> {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient.post<Order>(this.currencyPreferenceService.appendCurrencyToUrl(`${aliUrl}client/order/order-object`), {
      ...basket,
    });
  }

  submit(order: OrderAsAuthorized) {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient
      .post<any>(this.currencyPreferenceService.appendCurrencyToUrl(`${aliUrl}client/order/submit-order-as-authorized`), order)
      .pipe(catchError(({ error }) => this.rethrow(error)));
  }

  submitUnauthorized(order: { order: any; basket: any }) {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient
      .post<any>(this.currencyPreferenceService.appendCurrencyToUrl(`${aliUrl}client/order/submit-order-as-unauthorized`), order)
      .pipe(catchError(({ error }) => this.rethrow(error)));
  }

  payments(): Observable<Payments> {
    const aliUrl = this.apiUrlService.getApiUrl();
    const clientId = ApiClientService.CACHED_CLIENT?.id;
    const params = clientId ? { params: { client_id: String(clientId) } } : {};

    return this.httpClient.get<Payments>(
      `${aliUrl}client/order/available-payments`,
      params
    );
  }

  refreshPaymentLink(orderId: string): Observable<PaymentLink> {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient.post<PaymentLink>(
      `${aliUrl}client/refresh-payment-link/${orderId}`,
      null,
    );
  }

  private rethrow = (error: any) => {
    if (error === undefined || error === null) {
      return;
    }
    if (error.hasOwnProperty('errors')) {
      const values = Object.values(error.errors);
      return throwError(values);
    } else {
      // zakomentowałem to dlatego że przy przekierowaniach niepotrzebnie nam sie to pokazuje
      // a błąd z backendu który nie ma property errors nie powinien sie pokazywać userowi
      // return throwError('Unknown error');
    }
  };

  /**
   * ZADANIE B5.4. Pomocnik dokładający nagłówek autoryzacji ZNIKNĄŁ — robi to teraz
   * interceptor uwierzytelniania z modułu głównego, dla wszystkich żądań naraz.
   *
   * Zmiana ma jeden konkretny skutek: odpowiedź 401 na tych trasach uruchamia ODNOWIENIE
   * sesji i ponowienie żądania, a nie cichą odmowę. Wcześniej token jechał ręcznie, więc
   * żądania działały — ale nikt nie łapał wygaśnięcia, a interceptor, który miał to robić,
   * był zarejestrowany w łańcuchu, z którego nie korzystał żaden serwis.
   */
}
